/**
 * The contact a guest typed into a Guest Checkout form, remembered on the studio's origin for
 * 10 minutes so the form re-opens filled after a failed attempt and sign-up can prefill it.
 * Consents are never stored: the guest accepts the terms again every time.
 */
export type GuestContactDraft = {
    firstName: string
    lastName: string
    email: string
    phone: string
}

const STORAGE_KEY = 'fitro.guestContactDraft'
const TTL_MS = 10 * 60 * 1000

export function saveGuestContactDraft(draft: GuestContactDraft): void {
    try {
        const {firstName, lastName, email, phone} = draft
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
            firstName, lastName, email, phone, expiresAt: Date.now() + TTL_MS,
        }))
    } catch {
        // Storage can be unavailable (private mode); the form simply starts empty next time.
    }
}

export function readGuestContactDraft(): GuestContactDraft | null {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY)
        if (!raw) return null
        const value: unknown = JSON.parse(raw)
        const draft = value && typeof value === 'object' ? value as Record<string, unknown> : {}
        const fields = [draft.firstName, draft.lastName, draft.email, draft.phone]
        if (typeof draft.expiresAt !== 'number' || draft.expiresAt <= Date.now()
            || !fields.every(field => typeof field === 'string')) {
            clearGuestContactDraft()
            return null
        }
        return {
            firstName: draft.firstName as string,
            lastName: draft.lastName as string,
            email: draft.email as string,
            phone: draft.phone as string,
        }
    } catch {
        clearGuestContactDraft()
        return null
    }
}

export function clearGuestContactDraft(): void {
    try {
        window.localStorage.removeItem(STORAGE_KEY)
    } catch {
        // Nothing to clear when storage is unavailable.
    }
}

/** Drops an expired draft; the public studio pages call it on load so the contact does not linger. */
export function clearExpiredGuestContactDraft(): void {
    readGuestContactDraft()
}
